import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import type { OrderEmsPush } from './useGetOrderDetail';

/**
 * EMS push lifecycle. Defined here (shared EMS domain) so the exports feature
 * depends on shared hooks, never the other way around.
 */
export type OrderStatus = 'pending' | 'submitted';

export interface Marketplace {
    marketplace: string,
    directory: string,
    displayName: string;
    estimator: string;
    selected: boolean;
}

export interface EMSPart {
    _id: string;
    type: string;
    partNumber: string;
    partName: string;
    msrp: number;
    listPrice: number;
    quantity: number;
    lineNumber: number;
    adjustable: boolean;
    adjusted?: boolean;
    adjustedPrice?: number;
}

export interface GetEmsResponse {
    parts: Array<EMSPart>;
    title: string;
    status: OrderStatus;
    vin: string;
    timeReceived: Date;
    marketplaces: Array<Marketplace>;
    location: string;
    roNumber: string;
    customer: string;
    insurance: string;
    estimator: string;
    numMarketplaces: number;
    canSwitchLocation: boolean;
    needsQuoteRequestId?: string;
    orderId?: string;
    /** Set when this EMS was submitted with a quote request (GET /ems/detail, ENG-2034). */
    quoteRequestId?: string;
    /** True when this EMS is the order's current EMS (GET /ems/detail, ENG-2034). */
    isCurrentOrderEms?: boolean;
    /**
     * Every EMS export for this workfile (newest first) — the RO's export
     * ledger, present with or without an order. Optional so the app degrades
     * against an API that predates the field.
     */
    pushes?: Array<OrderEmsPush>;
}

const getEms = async (sessionId: string, emsId: string): Promise<GetEmsResponse> => {
    const { data } = await axios.get(`${API_URL}/ems/detail`, {
        headers: { Authorization: sessionId },
        params: { emsId },
    }
    );
    return data;
};

export const useGetEms = (emsId: string, isOpen: boolean) => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: ['/ems/get', emsId],
        queryFn: () => getEms(sessionContext?.sessionId ?? '', emsId),
        enabled: !!emsId && isOpen
    });
}; 